import type { MediaType } from '@/types/media/mediaType';

import type { GraphDataPoint, LifeSpanData, LifeSpanDataPoint } from '@/lib/analytics/charts/type';

class LifeSpanService {
    /**
     * Merges current and previous media lifespan data for chart comparison
     * @param currentLifeSpanData - Current media lifespan data points
     * @param previousLifeSpanData - Previous media lifespan data points for comparison
     * @param metricKey - Metric to extract (like_count, reply_count, retweet_count)
     * @returns Chart-ready data points with value and previous_value
     */
    merge(
        currentLifeSpanData: LifeSpanData,
        previousLifeSpanData: LifeSpanData,
        metricKey: string = 'like_count'
    ): GraphDataPoint[] {
        if (!Array.isArray(currentLifeSpanData) || currentLifeSpanData.length === 0) {
            return [];
        }

        const graphData: GraphDataPoint[] = [];

        // Index previous data by timestamp for O(1) lookup during merge
        const previousDataMap = new Map<string, LifeSpanDataPoint>();
        if (Array.isArray(previousLifeSpanData)) {
            previousLifeSpanData.forEach((point) => {
                previousDataMap.set(point.time, point);
            });
        }

        currentLifeSpanData.forEach((currentPoint) => {
            const previousPoint = previousDataMap.get(currentPoint.time);

            const dataPoint: GraphDataPoint = {
                time: currentPoint.time,
                value: (currentPoint[metricKey as keyof LifeSpanDataPoint] as number) ?? 0
            };

            if (previousPoint && metricKey in previousPoint) {
                dataPoint.previous_value = previousPoint[
                    metricKey as keyof LifeSpanDataPoint
                ] as number;
            }

            graphData.push(dataPoint);
        });

        return graphData;
    }

    /**
     * Extracts navigation context for carousel (previous, current, next media)
     * @param medias - List of all available medias
     * @param currentMediaId - Currently selected media ID
     * @returns Navigation object with previous, current, and next media
     */
    getMediasData(
        medias: MediaType[],
        currentMediaId: string | null
    ): { previous_media?: MediaType; media?: MediaType; next_media?: MediaType } {
        if (!medias || medias.length === 0) {
            return {};
        }

        const currentIndex = medias.findIndex(
            (m) => m.id?.toString() === currentMediaId?.toString()
        );

        if (currentIndex === -1) {
            // Fallback to first media when selection is invalid
            return {
                media: medias[0],
                next_media: medias[1]
            };
        }

        return {
            previous_media: currentIndex > 0 ? medias[currentIndex - 1] : undefined,
            media: medias[currentIndex],
            next_media: currentIndex < medias.length - 1 ? medias[currentIndex + 1] : undefined
        };
    }

    /**
     * Gets the ID of the previous media in the list for comparison data fetching
     * @param medias - List of all available medias
     * @param currentMediaId - Currently selected media ID
     * @returns Previous media ID or null if at the beginning
     */
    getPreviousMediaId(medias: MediaType[], currentMediaId: string | null): string | null {
        if (!medias || medias.length === 0) {
            return null;
        }

        const currentIndex = medias.findIndex(
            (m) => m.id?.toString() === currentMediaId?.toString()
        );
        return currentIndex > 0 ? medias[currentIndex - 1]?.id : null;
    }
}

const lifeSpanService = new LifeSpanService();

export { lifeSpanService as LifeSpanService };
